import { ReactNode } from 'react';
import { useSwipe, SwipeState } from '@/hooks/use-swipe';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

export interface SwipeCardProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onTap?: () => void;
  disabled?: boolean;
  className?: string;
  leftLabel?: string;
  rightLabel?: string;
  leftColor?: string;
  rightColor?: string;
  isTopCard?: boolean;
}

export function SwipeCard({
  children,
  onSwipeLeft,
  onSwipeRight,
  onTap,
  disabled = false,
  className,
  leftLabel = 'Skip',
  rightLabel = 'Complete',
  leftColor = 'bg-muted',
  rightColor = 'bg-success',
  isTopCard = true,
}: SwipeCardProps) {
  const [state, handlers, reset] = useSwipe({
    onSwipeLeft,
    onSwipeRight,
    disabled,
    threshold: 100,
  });

  const handleClick = (e: React.MouseEvent) => {
    // Only trigger tap if not dragging
    if (!state.isDragging && state.x === 0) {
      onTap?.();
    }
  };

  const getCardStyle = (state: SwipeState): React.CSSProperties => ({
    transform: `translate(${state.x}px, ${state.y}px) rotate(${state.rotation}deg)`,
    opacity: state.opacity,
    transition: state.isAnimating ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
    cursor: state.isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
  });

  // Calculate indicator opacity based on swipe direction
  const leftIndicatorOpacity = state.direction === 'left' ? Math.min(1, Math.abs(state.x) / 100) : 0;
  const rightIndicatorOpacity = state.direction === 'right' ? Math.min(1, state.x / 100) : 0;

  return (
    <div
      className={cn(
        'relative rounded-2xl bg-card border border-border shadow-lg overflow-hidden',
        'will-change-transform select-none',
        isTopCard ? 'z-10' : 'z-0',
        className
      )}
      style={getCardStyle(state)}
      onClick={handleClick}
      {...handlers}
    >
      {/* Left indicator (Skip) */}
      <div
        className={cn(
          'absolute top-4 left-4 px-4 py-2 rounded-full font-bold text-sm',
          'flex items-center gap-2 border-2',
          'transition-opacity duration-150',
          leftColor,
          'border-muted-foreground/30'
        )}
        style={{ opacity: leftIndicatorOpacity }}
      >
        <X className="h-4 w-4" />
        {leftLabel}
      </div>

      {/* Right indicator (Complete) */}
      <div
        className={cn(
          'absolute top-4 right-4 px-4 py-2 rounded-full font-bold text-sm',
          'flex items-center gap-2 border-2',
          'transition-opacity duration-150',
          rightColor,
          'text-success-foreground border-success/50'
        )}
        style={{ opacity: rightIndicatorOpacity }}
      >
        {rightLabel}
        <Check className="h-4 w-4" />
      </div>

      {/* Card content */}
      <div className="relative z-0">
        {children}
      </div>

      {/* Swipe direction hint overlay */}
      {state.isDragging && (
        <>
          <div
            className="absolute inset-0 bg-destructive/10 pointer-events-none transition-opacity"
            style={{ opacity: leftIndicatorOpacity * 0.3 }}
          />
          <div
            className="absolute inset-0 bg-success/10 pointer-events-none transition-opacity"
            style={{ opacity: rightIndicatorOpacity * 0.3 }}
          />
        </>
      )}
    </div>
  );
}
